import React, { useEffect, useRef } from 'react';

/**
 * Local development stub for @user-interfaces/evo-app-container.
 *
 * In production the Evo shell provides this module via its import map.
 * During local development this stub is resolved instead (see the Vite
 * resolve alias in vite.config.ts and the resolveId plugin in
 * app-prod/vite.config.ts).
 *
 * The stub renders the underlying oc-component directly by building the
 * OC registry URL from the componentParams, replicating the behaviour of
 * the real EvoMfeApp so the widget is visible during local development.
 */

export interface EvoMfeAppProps {
  componentName: string;
  componentVersion: string;
  componentParams?: Record<string, unknown>;
  className?: string;
}

/** OC Client script - loaded once globally. */
const OC_CLIENT_URL = 'https://mfe.workspace.accessacloud.com/oc-client/client.js';

function ensureOcClient(): void {
  if (document.querySelector(`script[src="${OC_CLIENT_URL}"]`)) return;
  const script = document.createElement('script');
  script.src = OC_CLIENT_URL;
  script.async = true;
  document.head.appendChild(script);
}

export const EvoMfeApp: React.FC<EvoMfeAppProps> = ({
  componentName,
  componentVersion,
  componentParams,
  className,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    ensureOcClient();
  }, []);

  useEffect(() => {
    if (!containerRef.current) return;

    const params = new URLSearchParams();
    if (componentParams) {
      for (const [key, value] of Object.entries(componentParams)) {
        if (value !== undefined && value !== null) {
          params.set(key, String(value));
        }
      }
    }

    const baseUrl = `https://mfe.workspace.accessacloud.com/registry/${componentName}/${componentVersion}/`;
    const href = params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl;

    // Render an <oc-component> element so the OC client picks it up.
    containerRef.current.innerHTML = '';
    const oc = document.createElement('oc-component');
    oc.setAttribute('href', href);
    containerRef.current.appendChild(oc);

    // If the OC client is already loaded, tell it to render the new component.
    if (typeof (window as any).oc !== 'undefined') {
      (window as any).oc.renderUnloadedComponents();
    }
  }, [componentName, componentVersion, componentParams]);

  return (
    <div
      ref={containerRef}
      data-evo-mfe-app
      data-component-name={componentName}
      data-component-version={componentVersion}
      className={className}
    />
  );
};
